/**
 * HealthInsuranceSection — list + add/edit/remove + save for a patient's
 * health insurance policies.
 *
 * Drops into Contact Details (per ticket scope). Owns its server state
 * via `useHealthInsuranceList(patientId)` and saves via the existing
 * `useSaveHealthInsurance` mutation (wire-shape locked + verified
 * against a dev curl 2026-05-11 in PROD-715).
 *
 * Mirrors `EmergencyContactsSection` structure but with the
 * health-insurance specifics:
 *   - Single-primary invariant (only one `isPrimary` row)
 *   - Duplicate policy-number validation (Flutter parity — toast error)
 *   - Save calls `saveInsuranceList(patientId, rows)` — atomic full-list
 *
 * Form fields (Flutter parity): card photos (front/back), provider
 * (typeahead), policy number, eligibility/expiration dates, co-pay
 * type + per-billing-group breakdown, and a verbatim confirmation
 * checkbox. isPrimary is toggled at the list level (single-primary
 * invariant). Card photos + co-pay were the F-PAT-243 deep-edit gap,
 * now built via the shared payer components.
 */

import { useEffect, useState } from 'react';
import { RecordListAdd, RecordListEmpty } from '@/shared/ui/components/record-row-card';
import { Button } from '@scripsteam/scrips-signal-ds';
import { Skeleton } from '@scripsteam/scrips-signal-ds';
import { toast } from 'sonner';
import { useHealthInsuranceList } from '@/patients/services/use-health-insurance-list';
import { useSaveHealthInsurance } from '@/patients/services/use-save-health-insurance';
import { useBillingSettings } from '@/billing/services/use-billing-settings';
import { useAuthStore } from '@/auth/auth-store';
import { HealthInsuranceRowComponent, type InsuranceRow } from './health-insurance-row';
import type { HealthInsurance } from '@/services/scheduling/use-prebook-selected-patient';
import { useUnsavedChangesSource } from '@/shared/ui/components/unsaved-changes';

export interface HealthInsuranceSectionProps {
  patientId: string | null | undefined;
  /** Tenant context for the insurance-payer search endpoint
   *  (`useSearchInsurancePayers` requires it). Caller supplies. */
  organizationId: string;
  readOnly?: boolean;
}

export function HealthInsuranceSection({
  patientId,
  organizationId,
  readOnly,
}: HealthInsuranceSectionProps) {
  // PROD-2743 · switch ON: the DS `add` glyph (one icon system, 16px inline); OFF = today's lucide.
  const { data: serverList, isLoading } = useHealthInsuranceList(patientId);
  const saveMutation = useSaveHealthInsurance();
  const billingSettings = useBillingSettings(organizationId);
  const billingGroups = billingSettings.data?.billingGroups ?? [];
  const currencyCode =
    useAuthStore((s) => s.user?.organizationCurrency) ?? 'AED';
  const [rows, setRows] = useState<InsuranceRow[]>([]);
  const [isDirty, setIsDirty] = useState(false);

  // Hydrate when the server snapshot arrives / refetches.
  useEffect(() => {
    // Array.isArray for the same reason as the emergency-contacts section (PROD-1988).
    if (!Array.isArray(serverList)) return;
    setRows(serverList.map((r) => ({ ...r, isEdit: false })));
    setIsDirty(false);
  }, [serverList]);

  const liveRowCount = rows.filter((r) => !r.isDeleted).length;

  const handleAdd = () => {
    if (readOnly) return;
    const next: InsuranceRow = {
      patientId: patientId ?? undefined,
      policyNumber: '',
      isPrimary: liveRowCount === 0,
      isVerified: false,
      isReviewed: true,
      isDeleted: false,
      isEdit: true,
    };
    setRows((prev) => [...prev, next]);
    setIsDirty(true);
  };

  const handleRowChange = (i: number, next: InsuranceRow) => {
    setRows((prev) => {
      const wasPrimary = prev[i]?.isPrimary ?? false;
      const isPromoting = !!next.isPrimary && !wasPrimary;
      return prev.map((r, idx) => {
        if (idx === i) return next;
        // Maintain single-primary invariant.
        if (isPromoting) return { ...r, isPrimary: false };
        return r;
      });
    });
    setIsDirty(true);
  };

  const handleCancel = (i: number) => {
    setRows((prev) => {
      const row = prev[i];
      if (!row) return prev;
      if (!row.healthInsuranceId) {
        // New row → drop.
        return prev.filter((_, idx) => idx !== i);
      }
      // Existing row → restore from server snapshot if available.
      const snapshot = (serverList ?? []).find(
        (s) => s.healthInsuranceId === row.healthInsuranceId,
      );
      if (snapshot) {
        return prev.map((r, idx) => (idx === i ? { ...snapshot, isEdit: false } : r));
      }
      return prev.map((r, idx) => (idx === i ? { ...r, isEdit: false } : r));
    });
  };

  const handleRemove = (i: number) => {
    if (readOnly) return;
    setRows((prev) => {
      const row = prev[i];
      if (!row) return prev;
      if (!row.healthInsuranceId) {
        return prev.filter((_, idx) => idx !== i);
      }
      // Soft-delete. If we removed the primary, promote the first live
      // row to primary (Flutter parity — different from emergency
      // contacts which doesn't auto-promote).
      const wasPrimary = !!row.isPrimary;
      const next = prev.map((r, idx) =>
        idx === i ? { ...r, isDeleted: true, isEdit: false } : r,
      );
      if (wasPrimary) {
        const firstLive = next.findIndex((r) => !r.isDeleted);
        if (firstLive >= 0) next[firstLive] = { ...next[firstLive], isPrimary: true };
      }
      return next;
    });
    setIsDirty(true);
  };

  const handleSave = async (): Promise<boolean> => {
    if (!patientId) {
      toast.error('Cannot save — patient not loaded yet.');
      return false;
    }
    if (rows.some((r) => r.isEdit && !r.isDeleted)) {
      toast.error('Finish editing rows before saving.');
      return false;
    }
    // Duplicate policy-number guard (Flutter parity).
    const liveRows = rows.filter((r) => !r.isDeleted);
    const seen = new Set<string>();
    for (const r of liveRows) {
      const key = (r.policyNumber ?? '').trim().toLowerCase();
      if (!key) continue;
      if (seen.has(key)) {
        toast.error(`Policy number "${r.policyNumber}" already used on another row.`);
        return false;
      }
      seen.add(key);
    }
    try {
      // Strip the UI-only `isEdit` flag before send.
      const payload: HealthInsurance[] = rows.map(({ isEdit: _isEdit, ...rest }) => rest);
      await saveMutation.mutateAsync({ patientId, insuranceList: payload });
      toast.success('Insurance policies saved');
      setIsDirty(false);
      return true;
    } catch {
      // Global error handler surfaces server errors.
      return false;
    }
  };

  // Cancel = back to the last saved list (the server snapshot the rows were hydrated from).
  const handleDiscard = () => {
    if (Array.isArray(serverList)) setRows(serverList.map((r) => ({ ...r, isEdit: false })));
    setIsDirty(false);
  };

  // Inside a page that owns the "Unsaved changes" bar, that bar saves and cancels; this section's own Save hides.
  const hosted = useUnsavedChangesSource({
    dirty: isDirty,
    saving: saveMutation.isPending,
    save: handleSave,
    cancel: handleDiscard,
  });


  const visibleRows = rows
    .map((row, i) => ({ row, i }))
    .filter(({ row }) => !row.isDeleted);

  if (!patientId) {
    return (
      <RecordListEmpty>Save the patient first before adding insurance policies.</RecordListEmpty>
    );
  }

  if (isLoading) {
    return (
      <div className="space-y-3">
        <Skeleton className="h-16 w-full" />
        <Skeleton className="h-16 w-full" />
      </div>
    );
  }

  return (
    // The approved design's PaRecordList: the rows 12 apart, the empty line, the "Add …" row at the foot.
    <div data-slot="record-list">
      {visibleRows.length === 0 ? (
        <>
          <RecordListEmpty>No insurance on file.</RecordListEmpty>
          {!readOnly ? <RecordListAdd onClick={handleAdd}>Add insurance</RecordListAdd> : null}
        </>
      ) : (
        <>
          {visibleRows.map(({ row, i }) => (
            <HealthInsuranceRowComponent
              key={row.healthInsuranceId || `new-${i}`}
              row={row}
              isOnly={visibleRows.length <= 1}
              organizationId={organizationId}
              billingGroups={billingGroups}
              currencyCode={currencyCode}
              readOnly={readOnly}
              onChange={(next) => handleRowChange(i, next)}
              onCancel={() => handleCancel(i)}
              onRemove={() => handleRemove(i)}
            />
          ))}
          {/* Cap at 2 policies (primary + secondary) — Flutter only renders
              the Add affordance when `totalItem < 2`
              (health_insurance_widget.dart:294). F-PAT-243. */}
          {!readOnly && liveRowCount < 2 ? (
            <RecordListAdd onClick={handleAdd}>Add insurance</RecordListAdd>
          ) : null}
        </>
      )}

      {!readOnly && !hosted ? (
        <div className="flex justify-end pt-2">
          <Button
            type="button"
            size="sm"
            onClick={() => void handleSave()}
            disabled={
              !isDirty ||
              saveMutation.isPending ||
              rows.some((r) => r.isEdit && !r.isDeleted)
            }
          >
            {saveMutation.isPending ? 'Saving…' : 'Save insurance'}
          </Button>
        </div>
      ) : null}
    </div>
  );
}
